import { keyArray } from "@solid-primitives/keyed"
import { createEffect, createSignal, onCleanup, type Accessor } from "solid-js"
import { BoxGeometry, Color, ConeGeometry, Group, Mesh, MeshBasicMaterial, type Scene } from "three"
import type { Nullable } from "vite-node"
import isIdListSame from "../../../../rx/signal/equals/isIdListSame"
import type { Engine } from "../../../core/engine/Engine.type"
import type { MountainEntity } from "../../../core/entity/EntityType/Mountain"
import { isEntityMountain } from "../../../core/entity/entityTypeMetaList.generated"
import randomColorInt from "../../../core/player/randomColorInt"
import tmpRngWith from "../../../core/rng/tmpRngWith"
import coord from "../../../core/tile_position_xy/coord"
import getTilePositionMinMax from "../../../core/tile_position_xy/getTilePositionMinMax"
import growTilePositionMinMax from "../../../core/tile_position_xy/growTilePositionMinMax"
import { HALF_PI } from "../../../core/util/math/Math"
import { deepClone } from "../../../deep_clone"
import { isListSameXYID } from "../../../isListSameXYID"
import nullIfEmptyArray from "../../../ldsh/nullIfEmptyArray"
import { VERTICES_PER_TILE } from "../../consts"
import disposeRecursive from "../../fn/disposeRecursive"
import fadeOutMaterialAndCleanupObject3D from "../../fn/fadeOutMaterialAndCleanupObject3D"
import setObjPosition from "../../fn/setObjPosition"
import InwardPointedPlaneGeometry from "../mesh/InwardPointedPlaneGeometry.class"
import createInitialMountainClusters from "./createInitialMountainClusters"
import createMountainTerrainMesh from "./createMountainTerrainMesh"
import createMountainTerrainMeshV2 from "./createMountainTerrainMeshV2"

const FADE_DURATION = 1000
const SHOW_DEBUG_MARKERS = false

// pointing diagonally in the same direction
export default function createMountainTerrainEffectV2(
  engine: Engine,
  scene: Scene,
): void {
  // return
  // console.log('createMountainTerrainEffect')

  // let lastId = 200
  // const mountainEnts = [
  //   coord(3, 4),
  //   coord(3, 1),
  //   coord(5, 6),
  //   coord(6, 6),
  // ].map(({x, y}) => createMountainEntity(lastId++, x, y, MountainKind.Island))

  const [getMountainEnts, setMountainEnts] = createSignal<Array<MountainEntity>>([], {
    equals: isIdListSame,
  })

  createEffect(() => {
    setMountainEnts(engine.state.ents.filter(isEntityMountain))
  })


  function getChunks() {
    return createInitialMountainClusters(getMountainEnts())
  }

  // createEffect(() => {
  //   console.log('createInitialMountainClusters', createInitialMountainClusters(getMountainEnts()))
  // })

  // we'll use the first ID as the chunk ID
  function getChunkId(ents: Array<MountainEntity>) {
    return ents[0].id
  }

  // eslint-disable-next-line solid/reactivity
  const mapped = keyArray(getChunks, getChunkId, (
    getList,
    // getIndex
  ) => {
    // console.log('mapped', {index: getIndex(), chunk: getChunkId(getList())}, getList().length)
    // we must deep clone, unwrap will leave nested objects as proxies
    let prevList: Nullable<Array<MountainEntity>> = null

    let mesh: Nullable<Awaited<ReturnType<typeof createMountainTerrainMesh>>>

    // onMount(() => {
    //   console.log('mounted', {chunk: getChunkId(getList()), len: getList().length})
    // })

    onCleanup(async () => {
      // console.log('cleanup', {chunk: getChunkId(getList()), len: getList().length})
      fadeOutMaterialAndCleanupObject3D(mesh, FADE_DURATION)
    })

    // eslint-disable-next-line solid/reactivity
    createEffect(async () => {
      const nextList = getList()
      if (isListSameXYID(prevList, nextList)) {
        return
      }
      // console.log('updating', getChunkId(nextList), nextList.map(e => e.id))

      const prevMesh = mesh

      const nextMesh = await (nullIfEmptyArray(nextList) && createMountainTerrainMeshV2(nextList))
      if (nextMesh) {
        nextMesh.position.y += 1
        scene.add(nextMesh)
      }
      if (prevMesh) {
        prevMesh.visible = false
        prevMesh.removeFromParent()
        disposeRecursive(prevMesh)
      }
      // fadeOutMaterialAndCleanupObject3D(prevMesh, FADE_DURATION)
      mesh = nextMesh
      // mesh!.visible = false
      prevList = deepClone(nextList)
    })

    return getList
  })
  // we need this effect otherwise nothing calls the mapped function
  createEffect(mapped)

  if (SHOW_DEBUG_MARKERS) {
    const group = new Group()
    createEffect(() => {
      group.clear()
      mapped().forEach((getList: Accessor<Array<MountainEntity>>) => {
        const color = new Color(randomColorInt(tmpRngWith(getList()[0].id)))
        const mountainEnts = getList()
        console.log('getList', deepClone(mountainEnts))
        mountainEnts.forEach((ent: MountainEntity) => {
          const radius = VERTICES_PER_TILE / 3
          const height = VERTICES_PER_TILE / 1.5
          const geometry = new ConeGeometry(radius, height, 8)
          const material = new MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.5,
            wireframe: true,
          })
          const mesh = new Mesh(geometry, material)
          mesh.name = `MountainDebugCone-${ent.id}`
          group.add(mesh)
          setObjPosition(mesh, ent, 14 * (VERTICES_PER_TILE / 32))
        })

        mountainEnts.forEach((ent: MountainEntity) => {
          const size = VERTICES_PER_TILE * 1
          const segments = 4
          const geometry = new InwardPointedPlaneGeometry(size, size, segments, segments)
          geometry.rotateX(HALF_PI)
          const material = new MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.5,
            wireframe: true,
          })
          const mesh = new Mesh(geometry, material)
          mesh.name = `MountainDebugIWPP-${ent.id}`
          group.add(mesh)
          setObjPosition(mesh, ent, 4 * (VERTICES_PER_TILE / 32))
          mesh.visible = false
        })

        {
          const tpmm = getTilePositionMinMax(mountainEnts)
          const midPoint = coord((tpmm.minX + tpmm.maxX) / 2, (tpmm.minY + tpmm.maxY) / 2)
          const size = VERTICES_PER_TILE * 1
          const segments = 1
          const geometry = new BoxGeometry(size, size, size, segments)
          geometry.rotateX(HALF_PI)
          const material = new MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.5,
            wireframe: true,
          })
          const mesh = new Mesh(geometry, material)
          mesh.name = `MountainDebugMidPoint-${mountainEnts.map(e => e.id).join('-')}`
          group.add(mesh)
          setObjPosition(mesh, midPoint, 18 * (VERTICES_PER_TILE / 32))
          // mesh.visible = false
          {
            growTilePositionMinMax(tpmm, 1)
            const width = (1 + (tpmm.maxX - tpmm.minX)) * VERTICES_PER_TILE
            const height = (1 + (tpmm.maxY - tpmm.minY)) * VERTICES_PER_TILE
            const depth = size / 10
            const geometry = new BoxGeometry(width, depth, height, segments)
            // geometry.rotateX(HALF_PI)
            const mesh = new Mesh(geometry, material)
            group.add(mesh)
            setObjPosition(mesh, midPoint, (5) * (VERTICES_PER_TILE / 32))

          }
        }

      })
    })
    scene.add(group)
  }

}
